/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type AbstractControl, Validators } from '@angular/forms';
import { type Command, type PromptConfigFull, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type FileSystem, type NFSClient } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';

import { type NFSClientVm } from '../../../share';
import { ClientType } from '../../nfs-share.enum';
import { NfsShareStrService } from '../../nfs-share-str.service';
import { ANONYMOUS_ID_RANGE } from '../client-nfs-share.const';
import { ClientNfsShareService } from '../client-nfs-share.service';

@Component({
    selector: 'app-modify-client-nfs-share',
    templateUrl: './modify-client-nfs-share.component.html',
    styleUrls: ['./modify-client-nfs-share.component.scss'],
})
export class ModifyClientNfsShareComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 待修改的Client，前后台都要传入
     */
    @Input() data: NFSClient;

    /**
     * 是否是前台表格的按钮
     */
    @Input() isFrontTable = false;
    /**
     * 前台表格传入的clients，用于校验重复，后端表格下发接口校验
     */
    @Input() clients: NFSClient[] = [];
    /**
     * 前台表格完成处理之后，emit数据回去
     */
    @Output() callBack: EventEmitter<NFSClient> = new EventEmitter();
    @Input() entry: FileSystemEntryE;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;

    /**
     * 匿名用户ID取值范围
     */
    ANONYMOUS_ID_RANGE = ANONYMOUS_ID_RANGE;
    ProductConfig = ProductConfig;

    i18n = this.str.i18n;
    ClientType = ClientType;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    vm: FormGroup;
    errorMessage: any;

    /**
     * 参与判断的表单字段，用于判断NFS共享UNIX权限、kerberos相关权限表单项是否存在读写权限
     */
    toJudgeWriteModeShowArr: string[] = ['unixPermission', 'kerberos5Permission', 'kerberos5iPermission', 'kerberos5pPermission'];

    FileSystemEntryE = FileSystemEntryE;

    get changedRawValue(): NFSClientVm {
        return this.vm ? this.vm.changedRawValue : null;
    }

    /**
     * 权限级别选项，默认是完整的
     */
    permissionLevelOpts: SelectOption[] = this.str.permissionLevelOpts;

    /**
     * 高级属性是否展开
     */
    advanced = false;

    constructor(
        public str: NfsShareStrService,
        private service: ClientNfsShareService,
        public utilService: UtilService,
        private toolService: ToolService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        let auditFsOpenWriteSwitch = false;

        if (this.fileSystem) {
            auditFsOpenWriteSwitch = this.fileSystem.isAuditLogFs && this.fileSystem.auditLogWritable;
        }

        // 审计日志文件系统下的NFS共享添加客户端的权限有限制
        if (this.entry === FileSystemEntryE.AuditLog && !auditFsOpenWriteSwitch) {
            this.permissionLevelOpts = this.str.auditLogFsPermissionLevelOpts;
        }

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        const tempControls = {
            // 主机名称
            hostName: new FormControl(this.data.NAME, [
                Validators.required, this.hostNameValidator(), this.repeatValidator(),
            ]),
            // 网络组名称
            netName: new FormControl(this.data.NAME.substr(1), [
                Validators.required, this.networkGroupValidator(), this.repeatValidator(), DMValidators.maxByteLength(254),
            ]),
            // 权限级别
            unixPermission: new FormControl(this.data.ACCESSVAL, []),
            kerberos5Permission: new FormControl(this.data.ACCESSKRB5, []),
            kerberos5iPermission: new FormControl(this.data.ACCESSKRB5I, []),
            kerberos5pPermission: new FormControl(this.data.ACCESSKRB5P, []),
            // 权限限制
            all_squash: new FormControl(this.data.ALLSQUASH, []),
            // root权限限制
            root_squash: new FormControl(this.data.ROOTSQUASH, []),
            // 源端口校验限制
            secure: new FormControl(this.data.SECURE, []),
            anonymousUserId: new FormControl(this.data.ANONYMOUSID),
        };

        // 如果是后台表格，不能修改名字
        if (!this.isFrontTable) {
            delete tempControls.hostName;
            delete tempControls.netName;
        }

        // 如果是网络组或者主机，要删除其他的form
        if (this.data._type === ClientType.net) {
            delete tempControls.hostName;
        } else if (this.data._type === ClientType.host) {
            delete tempControls.netName;
        }
        this.vm = new FormGroup(tempControls, { updateOn: 'blur' });

        this.errorMessage = {
            hostName: {
                required: this.str.requiredErr(this.i18n.label.client),
                hostName: this.i18n.error.hostName,
                nameRepeat: this.str.repeatErr(this.i18n.label.client),
            },
            netName: {
                required: this.str.requiredErr(this.i18n.label.name),
                maxByteLength: this.str.rangeByteLengthErr(1, 254),
                networkGrupName: this.i18n.error.netName,
                nameRepeat: this.str.repeatErr(this.i18n.label.name),
            },
        };
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];

        // 只有后台表格需要有高危提示
        if (!this.isFrontTable &&
            this.vm.changedValue &&
            Object.prototype.hasOwnProperty.call(this.vm.changedValue, 'anonymousUserId')
        ) {
            prompts.push({
                id: 'prompt-modify-client-nfs-share-highRisk-modifyAnonymousId',
                opType: 'warning',
                title: this.i18n.title.modifyClient,
                content: this.i18n.highRisk.modifyAnonymousId,
                width: 700,
            });
        }

        return prompts;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];

        // 前台表格不需要cmds，只用emit数据回去
        // 先收集数据
        const changeData = this.collectionClient();
        if (this.isFrontTable) {
            this.callBack.emit(changeData);
        } else {
            cmds.push(this.buildNFSClientCmd(changeData));
        }
        return cmds;
    }

    collectionClient(): NFSClient {
        const params: NFSClient = {};
        const changedValue = this.changedRawValue;

        if (Object.prototype.hasOwnProperty.call(changedValue, 'hostName')) {
            params.NAME = (changedValue.hostName as string);
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'netName')) {
            params.NAME = `@${changedValue.netName}`;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'unixPermission')) {
            params.ACCESSVAL = changedValue.unixPermission;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'kerberos5Permission')) {
            params.ACCESSKRB5 = changedValue.kerberos5Permission;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'kerberos5iPermission')) {
            params.ACCESSKRB5I = changedValue.kerberos5iPermission;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'kerberos5pPermission')) {
            params.ACCESSKRB5P = changedValue.kerberos5pPermission;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'all_squash')) {
            params.ALLSQUASH = changedValue.all_squash;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'root_squash')) {
            params.ROOTSQUASH = changedValue.root_squash;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'secure')) {
            params.SECURE = changedValue.secure;
        }
        if (Object.prototype.hasOwnProperty.call(changedValue, 'anonymousUserId')) {
            params.ANONYMOUSID = changedValue.anonymousUserId;
        }

        if (this.data.vstoreId) {
            params.vstoreId = this.data.vstoreId;
        }

        return params;
    }

    buildNFSClientCmd(changeData: NFSClient): Command {
        const typeStr = this.data._type === ClientType.host ? this.i18n.value.host : this.i18n.value.net;
        return {
            name: this.i18n.rest.modifyClient,
            nameParams: [typeStr, this.data.NAME.replace('@', '')],
            action: this.service.modify(this.data.ID, changeData),
        };
    }

    /**
     * 当选择【主机】时，校验规则
     * 主机名称格式要求如下：
     * 1. 主机名称长度范围为1到255，由字母、数字、“-”、“.”、“_”组成。
     * 2. 只能以字母或数字开头，不能以“_”或“-”结尾。
     * 3. 不能包含连续的“.”，不能包含“_.”、“._”、“-.”或“.-”，不能为纯数字。
     *
     * IP地址格式要求如下：
     * 1. 支持输入单个客户端IP地址、客户端IP地址段或使用“*”表示全部客户端IP地址。
     * 2. 支持输入IPv4、IPv6地址或两者的混合IP地址。
     * 3. IPv4的掩码部分取值范围为1~32，IPv6的掩码部分取值范围为1~128。示例：192.168.0.10或192.168.0.0/24或*。
     */
    hostNameValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }
            // 若当前类型为网络组
            if (this.data._type === ClientType.net) {
                return null;
            }
            const value = c.value;
            let valid = true;

            if (!this.hostNameIpValid(value) && !this.hostNameStrValid(value)) {
                valid = false;
            }

            return valid ? null : { hostName: true };
        };
    }

    // hostname 非ip校验
    hostNameStrValid(value: string): boolean {
        // 长度1-255
        if (value.length > 255) {
            return false;
        }

        // 不能以字符串'-'开头
        if (value.charAt(0) === '-') {
            return false;
        }

        // 不能含有空格
        if (value.indexOf(' ') > -1) {
            return false;
        }
        return true;
    }

    // hostname ip校验,
    hostNameIpValid(value: string): boolean {
        if (value === '*') {
            return true;
        }
        if (!DMValidators.validateIpv4(value) || !DMValidators.validateIpv6(value)) {
            // 是ip
            return true;
        }

        // 不是ip，校验是否带子网掩码
        if (value.indexOf('/') < 0) {
            // 既不是ip也没有带子网掩码
            return false;
        }
        const strArr = value.split('/');
        if (strArr.length > 2) {
            // 不是正常格式
            return false;
        }
        const validIpv4 = !DMValidators.validateIpv4(strArr[0]);
        const validIpv6 = !DMValidators.validateIpv6(strArr[0]);

        const reg = /^[0-9]*$/;

        // 不全是数字
        if (!strArr[1] || !reg.test(strArr[1])) {
            return false;
        }
        const num = Number(strArr[1]);
        if (validIpv4 && num >= 1 && num <= 32) {
            return true;
        }
        if (validIpv6 && num >= 1 && num <= 128) {
            return true;
        }
        return false;
    }

    /**
     * 网络组校验规则:
     * 长度为1到254位，由字母，数字、“_”，“-”，“.”，以及中文字符组成。
     */
    networkGroupValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }

            // 若当前类型为主机
            if (this.data._type === ClientType.host) {
                return null;
            }
            const value = c.value;
            let valid = true;

            // 正则校验
            const reg =
                /^[a-zA-Z0-9\u4e00-\u9fa5\u3002\uff1f\uff01\uff0c\u3001\uff1b\uff1a\u201c\u201d\u2018\u2019\uff08\uff09\u300a\u300b\u3008\u3009\u3010\u3011\u300e\u300f\u300c\u300d\ufe43\ufe44\u3014\u3015\u2026\u2014\uff5e\ufe4f\uffe5\-._]*$/;
            if (!reg.test(value)) {
                valid = false;
            }

            return valid ? null : { networkGrupName: true };
        };
    }

    // 不能跟列表中已有选项重复,只有前端表格才会校验，因为后端表格clients是个空数组，返回null
    repeatValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }
            const value = c.value;

            // 如果值没有改变，就return
            if (this.data.NAME === value || this.data.NAME.replace('@', '') === value) {
                return null;
            }
            for (let i = 0; i < this.clients.length; i++) {
                const clientObj = this.clients[i];

                // 如果相等或去掉@并且类型是net，那就重复
                const nameIsRepeat = value === clientObj.NAME ||
                    (value === clientObj.NAME.replace('@', '') && clientObj._type === ClientType.net);

                // 如果是相同类型才比较
                if (clientObj._type === this.data._type && nameIsRepeat) {
                    return { nameRepeat: true };
                }
            }
            return null;
        };
    }
}
